import { Archive, Ban, MoreHorizontal, Pencil, Plus, RefreshCw, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Panel, PanelLabel } from "@/components/PageHeader";
import { money, moneyIn } from "@/lib/format";
import { FxBalance } from "@/components/accounts/FxBalance";
import { SUBTYPE_LABELS, type Account } from "@/lib/finance";
import type { AssetAction } from "@/components/accounts/AssetActionDialog";

interface Props { accounts: Account[]; total: number; onBuy: () => void; onAdd: () => void; onEdit: (a: Account) => void; onArchive: (a: Account, archived: boolean) => void; onAction: (a: AssetAction) => void }
const fmtDate = (d: string) => d.split("-").reverse().join("/");

function Actions({ a, onEdit, onArchive, onAction }: { a: Account } & Pick<Props, "onEdit" | "onArchive" | "onAction">) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-[#1B2A4A]" aria-label="Acciones" data-testid={`asset-actions-${a.id}`}><MoreHorizontal className="h-4 w-4" /></button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48 bg-white">
        {!a.disposed_on && <DropdownMenuItem onSelect={() => onAction({ kind: "revalue", asset: a })} className="cursor-pointer" data-testid={`asset-revalue-${a.id}`}><RefreshCw className="mr-2 h-4 w-4" /> Actualizar valor</DropdownMenuItem>}
        {!a.disposed_on && <DropdownMenuItem onSelect={() => onAction({ kind: "dispose", asset: a })} className="cursor-pointer" data-testid={`asset-dispose-${a.id}`}><Ban className="mr-2 h-4 w-4" /> Dar de baja</DropdownMenuItem>}
        <DropdownMenuItem onSelect={() => onEdit(a)} className="cursor-pointer" data-testid={`asset-edit-${a.id}`}><Pencil className="mr-2 h-4 w-4" /> Editar</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => onArchive(a, true)} className="cursor-pointer" data-testid={`asset-archive-${a.id}`}><Archive className="mr-2 h-4 w-4" /> Archivar</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Detail({ a }: { a: Account }) {
  if (a.disposed_on) return <p className="text-xs text-slate-500">Dado de baja el {fmtDate(a.disposed_on)}</p>;
  const loan = a.meta.linked_loan;
  const m = (n: number) => (loan?.in_mxn ? money(n) : moneyIn(n, a.currency));
  const dep = a.depreciation_on ? ` · pierde ${Math.round((a.depreciation_rate ?? 0.15) * 1000) / 10}% al año` : "";
  return (
    <p className="text-xs text-slate-500" data-testid={`asset-detail-${a.id}`}>
      {loan ? <>Vale {m(loan.in_mxn ? a.balance_mxn : a.current_balance)} · debes <span className="text-[#B5532F]">{m(loan.owed)}</span> · tuyo <b className="text-[#16294D]">{m(a.meta.equity ?? 0)}</b></> : `${SUBTYPE_LABELS[a.account_subtype ?? "other_asset"]}${dep}`}
    </p>
  );
}

export function GoodsCard({ accounts, total, onBuy, onAdd, ...h }: Props) {
  return (
    <Panel testId="goods-card">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
        <PanelLabel>Tus bienes</PanelLabel>
        <p className="figure-lg text-[#16294D]" data-testid="goods-total">{money(total)}</p>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button onClick={onBuy} className="h-10 bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="buy-asset-button"><ShoppingBag className="mr-2 h-4 w-4" /> Comprar un bien</Button>
        <Button variant="outline" onClick={onAdd} className="h-10 border-[#DDE1E8] text-[#1B2A4A]" data-testid="add-asset-button"><Plus className="mr-2 h-4 w-4" /> Agregar uno que ya tienes</Button>
      </div>
      {accounts.length === 0 && <p className="py-6 text-sm text-slate-500" data-testid="goods-empty">Aún no registras bienes: casa, terreno o auto.</p>}
      <ul className="mt-2 divide-y divide-[#DDE1E8]">
        {accounts.map((a) => (
          <li key={a.id} className={`grid grid-cols-[1fr_auto_28px] items-center gap-3 py-3.5 ${a.disposed_on ? "opacity-60" : ""}`} data-testid={`asset-row-${a.id}`}>
            <div className="min-w-0">
              <p className="break-words font-semibold leading-snug text-[#1B2A4A]">{a.name}</p>
              <Detail a={a} />
            </div>
            <span data-testid={`asset-value-${a.id}`}><FxBalance a={a} /></span>
            <Actions a={a} {...h} />
          </li>
        ))}
      </ul>
    </Panel>
  );
}
